// eslint-disable-next-line no-restricted-imports
import IdentityManager from '@arcgis/core/identity/IdentityManager';
import { logMapError } from './helpers/utils';

/** Mirrors EsriTokenView.source server-side: department-scoped credential, the shared fallback credential (no department credential configured), or that same fallback because a configured department credential was rejected by Esri when minting. */
export type EsriCredentialSource = 'DEPARTMENT' | 'GLOBAL' | 'GLOBAL_AFTER_DEPARTMENT_FAILURE';

/**
 * The stable contract vended by the backend token endpoint. Deliberately excludes the host
 * list to register against — which hosts a token applies to is a separate, evolving concern
 * (global today, per-department later) and is passed into register() explicitly instead.
 * webMapPortalId and source are distinct concerns (which webmap to load, and how the token
 * was resolved, not which hosts to trust) and are surfaced here as optional passthroughs —
 * webMapPortalId is only populated when source is DEPARTMENT; see EsriTokenView server-side.
 */
export interface EsriTokenWithExpiry {
    token: string;
    expiresInSeconds: number;
    webMapPortalId?: string;
    source?: EsriCredentialSource;
}

export type FetchEsriToken = () => Promise<EsriTokenWithExpiry>;

/** Re-mint once a token has consumed this share of its lifetime. Mirrors EsriTokenProvider's own refresh-ahead fraction on the backend. */
const REFRESH_FRACTION = 0.8;

/**
 * Retry cadence for a failed refresh. A short, fixed interval rather than a fresh
 * REFRESH_FRACTION-of-lifetime delay, so the exhaustion fallback engages within this
 * interval of the token's real expiry instead of lagging it by up to
 * (1 - REFRESH_FRACTION) x lifetime.
 */
const RETRY_DELAY_MS = 30_000;

/**
 * Registers a token with IdentityManager for every given Esri host, so the SDK attaches
 * it to matching requests automatically.
 */
export function register(token: string, expiresInSeconds: number, hosts: readonly string[]): void {
    const expires = Date.now() + expiresInSeconds * 1000;
    hosts.forEach((server) => {
        IdentityManager.registerToken({ server, token, expires });
    });
}

/**
 * Destroys every credential previously registered via register(). The SDK has no
 * per-server unregister, so this clears all Esri credentials app-wide — safe only because
 * this module is the sole registrant of Esri credentials in this app.
 */
export function unregister(): void {
    IdentityManager.destroyCredentials();
}

/**
 * Schedules a proactive re-fetch + re-register at ~80% of the current token's lifetime,
 * and reschedules itself off the newly granted lifetime each time. A failed refresh
 * retries after RETRY_DELAY_MS rather than ending the chain — the currently-registered
 * token stays in place in the meantime. Once that token's own expiry actually passes
 * without a successful refresh, `onRefreshExhausted` fires (repeatedly, on every
 * subsequent failed attempt) so the caller can fall back to another credential — the
 * retry loop itself never gives up. Returns a single stop function that cancels whatever
 * refresh is currently pending.
 *
 * Calling this again for the same token stream before invoking the previous stop() starts
 * a second, independent timer chain rather than replacing the first — the caller (e.g. a
 * React effect) is responsible for calling stop() before rescheduling, the same as with a
 * raw setInterval.
 */
export function scheduleRefresh(
    initialExpiresInSeconds: number,
    fetchToken: FetchEsriToken,
    onRegistered: (result: EsriTokenWithExpiry) => void,
    onRefreshError?: (error: unknown) => void,
    onRefreshExhausted?: () => void
): () => void {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let currentTokenExpiresAt = Date.now() + initialExpiresInSeconds * 1000;

    const runAfter = (delayMs: number) => {
        timeoutId = setTimeout(
            () => {
                fetchToken()
                    .then((result) => {
                        if (stopped) {
                            return;
                        }
                        currentTokenExpiresAt = Date.now() + result.expiresInSeconds * 1000;
                        onRegistered(result);
                        runAfter(result.expiresInSeconds * 1000 * REFRESH_FRACTION);
                    })
                    .catch((error) => {
                        if (stopped) {
                            return;
                        }
                        onRefreshError?.(error);
                        if (Date.now() >= currentTokenExpiresAt) {
                            onRefreshExhausted?.();
                        }
                        runAfter(RETRY_DELAY_MS);
                    });
            },
            Math.max(0, delayMs)
        );
    };

    runAfter(initialExpiresInSeconds * 1000 * REFRESH_FRACTION);

    return () => {
        stopped = true;
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
    };
}

/** HTTP statuses that mean a request was rejected for missing/invalid credentials. */
const AUTH_HTTP_STATUSES: ReadonlySet<number> = new Set([401, 403]);

/**
 * Whether an Esri request error is a credential failure. Message text alone can miss it -
 * the SDK sometimes throws an unrelated parsing exception on a 401 whose text never says
 * "token" - so this also checks the HTTP status.
 */
export function isEsriAuthError(error: {
    message?: string;
    details?: { httpStatus?: number };
}): boolean {
    return (
        (error.details?.httpStatus !== undefined &&
            AUTH_HTTP_STATUSES.has(error.details.httpStatus)) ||
        !!error.message?.toLowerCase().includes('token')
    );
}

export interface EsriErrorInterceptorOptions {
    urls: string[];
    /**
     * Called once when a request fails with a token-related error. This cannot replay the
     * failed request itself (the SDK's error callback has no such hook) — it triggers an
     * immediate out-of-cycle refresh so the SDK's own subsequent request/tile reload
     * succeeds, rather than us re-issuing the exact original call.
     */
    onTokenError?: () => void;
    /** Called once when the portal self-check request itself fails. */
    onPortalError?: () => void;
    /** Called once when a failure doesn't match the token or portal classification. */
    onBaseMapError?: () => void;
}

/**
 * Builds a single request-interceptor entry that classifies every failure across the
 * given urls and dispatches to the matching callback. `portal_error` (the portal
 * self-check request failing, often a symptom rather than the root cause) takes
 * precedence over `token_invalid`. `token_invalid` is a case-insensitive substring match
 * on the error message — a heuristic, not an enumerated set of known Esri error codes, so
 * it can both under- and over-match.
 */
export function createEsriErrorInterceptor({
    urls,
    onTokenError,
    onPortalError,
    onBaseMapError,
}: EsriErrorInterceptorOptions): __esri.RequestInterceptor {
    return {
        urls,
        error: (error) => {
            if (
                typeof error.details?.url === 'string' &&
                error.details.url.includes('sharing/rest/portals/self')
            ) {
                logMapError({
                    source: 'service',
                    event: 'portal_error',
                    error,
                });
                onPortalError?.();
                return;
            }

            const isTokenError = isEsriAuthError(error);
            if (isTokenError) {
                onTokenError?.();
            } else {
                onBaseMapError?.();
            }
            logMapError({
                source: 'service',
                event: isTokenError ? 'token_invalid' : 'basemap_load_error',
                error,
            });
        },
    };
}
